import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  MessageSquare,
  User,
  Building2,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Send,
  Filter,
  Eye,
  Wrench
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { tenantRequestsAPI } from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { toast } from 'sonner';
import ModuleTasksPanel from '../components/ModuleTasksPanel';

const statusLabels = {
  en_attente: 'En attente',
  en_cours: 'En cours',
  resolu: 'Resolu',
  rejete: 'Rejete'
};

const statusColors = {
  en_attente: 'bg-yellow-100 text-yellow-800',
  en_cours: 'bg-blue-100 text-blue-800',
  resolu: 'bg-green-100 text-green-800',
  rejete: 'bg-red-100 text-red-800'
};

const statusIcons = {
  en_attente: AlertCircle,
  en_cours: Clock,
  resolu: CheckCircle,
  rejete: XCircle
};

const categoryLabels = {
  maintenance: 'Maintenance',
  paiement: 'Paiement',
  document: 'Document',
  autre: 'Autre'
};

const TenantRequests = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Modal states
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isRespondOpen, setIsRespondOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [responding, setResponding] = useState(false);

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await tenantRequestsAPI.getAll();
      setRequests(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDetail = (request) => {
    setSelectedRequest(request);
    setIsDetailOpen(true);
  };

  const handleOpenRespond = (request) => {
    setSelectedRequest(request);
    setResponseText('');
    setNewStatus(request.status === 'en_attente' ? 'en_cours' : 'resolu');
    setIsRespondOpen(true);
  };

  const handleRespond = async () => {
    if (!responseText.trim()) {
      toast.error('Veuillez saisir une reponse');
      return;
    }

    setResponding(true);
    try {
      await tenantRequestsAPI.respond(selectedRequest.id, {
        response: responseText,
        status: newStatus
      });
      toast.success('Reponse envoyee avec succes');
      setIsRespondOpen(false);
      setSelectedRequest(null);
      setResponseText('');
      fetchRequests();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi de la reponse');
    } finally {
      setResponding(false);
    }
  };

  const filteredRequests = requests.filter(request => {
    const matchesSearch = 
      (request.tenant_name && request.tenant_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (request.property_title && request.property_title.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (request.subject && request.subject.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesStatus = filterStatus === 'all' || request.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  // Stats
  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'en_attente').length,
    inProgress: requests.filter(r => r.status === 'en_cours').length,
    resolved: requests.filter(r => r.status === 'resolu').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tenant-requests-page">
      <ModuleTasksPanel moduleKey="tenant_requests" moduleLabel="Demandes Locataires" />
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
          Demandes des Locataires
        </h1>
        <p className="text-gray-500 mt-1">
          Gerez les demandes et reclamations des locataires
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-[#1B3A4B] to-[#142D3A] text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <MessageSquare className="w-5 h-5" />
              </div>
              <div>
                <p className="text-white/70 text-sm">Total Demandes</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <AlertCircle className="w-5 h-5" />
              </div>
              <div>
                <p className="text-white/70 text-sm">En attente</p>
                <p className="text-2xl font-bold">{stats.pending}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Clock className="w-5 h-5" />
              </div>
              <div>
                <p className="text-white/70 text-sm">En cours</p>
                <p className="text-2xl font-bold">{stats.inProgress}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <CheckCircle className="w-5 h-5" />
              </div>
              <div>
                <p className="text-white/70 text-sm">Resolues</p>
                <p className="text-2xl font-bold">{stats.resolved}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Rechercher par locataire, bien ou sujet..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-requests-input"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[200px]" data-testid="filter-status-select">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="en_attente">En attente</SelectItem>
                <SelectItem value="en_cours">En cours</SelectItem>
                <SelectItem value="resolu">Resolu</SelectItem>
                <SelectItem value="rejete">Rejete</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Requests Table */}
      {filteredRequests.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Locataire</TableHead>
                  <TableHead>Bien</TableHead>
                  <TableHead>Categorie</TableHead>
                  <TableHead>Sujet</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRequests.map((request) => {
                  const StatusIcon = statusIcons[request.status] || AlertCircle;
                  return (
                    <TableRow key={request.id} className={request.status === 'en_attente' ? 'bg-yellow-50/50' : ''}>
                      <TableCell className="text-sm text-gray-600">
                        {formatDateTime(request.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">{request.tenant_name || 'N/A'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-gray-400" />
                          <span className="truncate max-w-[150px]">{request.property_title || 'N/A'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {categoryLabels[request.category] || request.category}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {request.subject}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[request.status]}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusLabels[request.status] || request.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenDetail(request)}
                            title="Voir les details"
                            data-testid={`view-request-${request.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {request.status !== 'resolu' && request.status !== 'rejete' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenRespond(request)}
                              title="Repondre"
                              className="text-[#1B3A4B] hover:text-[#142D3A]"
                              data-testid={`respond-request-${request.id}`}
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune demande</h3>
              <p className="text-gray-500">
                {searchQuery || filterStatus !== 'all'
                  ? 'Aucune demande ne correspond a vos criteres'
                  : 'Les demandes des locataires apparaitront ici'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detail Modal */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Details de la demande</DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Locataire</p>
                  <p className="font-medium">{selectedRequest.tenant_name}</p>
                  <p className="text-sm text-gray-500">{selectedRequest.tenant_phone}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Bien</p>
                  <p className="font-medium">{selectedRequest.property_title}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Categorie</p>
                  <Badge variant="outline">{categoryLabels[selectedRequest.category]}</Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Statut</p>
                  <Badge className={statusColors[selectedRequest.status]}>
                    {statusLabels[selectedRequest.status]}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Date de creation</p>
                  <p className="font-medium">{formatDateTime(selectedRequest.created_at)}</p>
                </div>
                {selectedRequest.updated_at !== selectedRequest.created_at && (
                  <div>
                    <p className="text-sm text-gray-500">Derniere mise a jour</p>
                    <p className="font-medium">{formatDateTime(selectedRequest.updated_at)}</p>
                  </div>
                )}
              </div>
              
              {/* Subject & Message */}
              <div className="border-t pt-4">
                <p className="text-sm text-gray-500 mb-1">Sujet</p>
                <p className="font-semibold text-lg">{selectedRequest.subject}</p>
              </div>
              
              <div>
                <p className="text-sm text-gray-500 mb-1">Message</p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="whitespace-pre-wrap">{selectedRequest.message}</p>
                </div>
              </div>
              
              {/* Response if exists */}
              {selectedRequest.response && (
                <div className="border-t pt-4">
                  <p className="text-sm text-gray-500 mb-1">Reponse de l'agence</p>
                  <div className="bg-[#1B3A4B]/5 p-4 rounded-lg border-l-4 border-[#1B3A4B]">
                    <p className="whitespace-pre-wrap">{selectedRequest.response}</p>
                    {selectedRequest.responded_by_name && (
                      <p className="text-sm text-gray-500 mt-2">
                        Par {selectedRequest.responded_by_name} le {formatDateTime(selectedRequest.responded_at)}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDetailOpen(false)}>
              Fermer
            </Button>
            {selectedRequest && !selectedRequest.linked_repair_id && (
              <Button
                variant="outline"
                onClick={() => {
                  // Pre-fill data via sessionStorage
                  sessionStorage.setItem('repair_prefill', JSON.stringify({
                    property_id: selectedRequest.property_id,
                    tenant_id: selectedRequest.tenant_id,
                    description: `[Demande ${selectedRequest.title || ''}] ${selectedRequest.description || ''}`,
                    source_request_id: selectedRequest.id,
                  }));
                  setIsDetailOpen(false);
                  navigate('/repairs?from_request=' + selectedRequest.id);
                }}
                className="border-[#D4A745] text-[#D4A745] hover:bg-[#D4A745]/10"
                data-testid={`create-repair-btn-${selectedRequest.id}`}
              >
                <Wrench className="w-4 h-4 mr-2" />
                Creer reparation
              </Button>
            )}
            {selectedRequest && selectedRequest.linked_repair_id && (
              <Button
                variant="outline"
                onClick={() => {
                  setIsDetailOpen(false);
                  navigate('/repairs');
                }}
                className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
              >
                <Wrench className="w-4 h-4 mr-2" />
                Voir reparation {selectedRequest.linked_repair_ref}
              </Button>
            )}
            {selectedRequest && selectedRequest.status !== 'resolu' && selectedRequest.status !== 'rejete' && (
              <Button
                onClick={() => {
                  setIsDetailOpen(false);
                  handleOpenRespond(selectedRequest);
                }}
                className="bg-[#1B3A4B] hover:bg-[#142D3A]"
              >
                <Send className="w-4 h-4 mr-2" />
                Repondre
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Respond Modal */}
      <Dialog open={isRespondOpen} onOpenChange={setIsRespondOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Repondre a la demande</DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-500">Demande de:</p>
                <p className="font-semibold">{selectedRequest.tenant_name}</p>
                <p className="text-sm font-medium mt-1">{selectedRequest.subject}</p>
              </div>
              
              <div>
                <Label htmlFor="response">Votre reponse *</Label>
                <Textarea
                  id="response"
                  value={responseText}
                  onChange={(e) => setResponseText(e.target.value)}
                  placeholder="Saisissez votre reponse..."
                  rows={5}
                  data-testid="response-textarea"
                />
              </div>
              
              <div>
                <Label>Nouveau statut</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger data-testid="new-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en_cours">En cours</SelectItem>
                    <SelectItem value="resolu">Resolu</SelectItem>
                    <SelectItem value="rejete">Rejete</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRespondOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleRespond}
              className="bg-[#1B3A4B] hover:bg-[#142D3A]"
              disabled={responding}
              data-testid="submit-response-btn"
            >
              {responding ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Envoi...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Envoyer la reponse
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantRequests;
